import {
  AdmissionResultRowDTO,
  AdmissionTierUniversityDTO,
  MentorQualityScoreDTO,
  QualityScoringRunDTO,
  SessionQualityScoreExportRowDTO,
  SessionQualityScoreRowDTO,
} from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import {
  ApiDescription,
  ApiData,
  EducationStrategyReason,
  GoalStatusTypes,
  RequestsMethod,
} from '../common';

type WeeklyMetric = {
  weekStart: string;
  weekEnd: string;
  mentorAllocations: number;
  mentorReallocations: number;
  rescheduledMeetings: number;
  completedSessions: number;
  payableSessions: number;
  strategistSessions: number;
  sessionSpend: number;
  salarySpend: number;
  totalSpend: number;
};

export const getMonthlyMetrics: ApiDescription = {
  endpoint: '/admin/metrics/monthly',
  method: RequestsMethod.Get,
};

export type GetMonthlyMetrics = ApiData<
  {
    month?: string;
  },
  {
    mentorAllocations: number;
    mentorReallocations: number;
    rescheduledMeetings: number;
    completedSessions: number;
    payableSessions: number;
    strategistSessions: number;
    totalSessionSpend: number;
    totalSalarySpend: number;
    totalSpend: number;
    averageWeeklySessionSpend: number;
    averageWeeklyTotalSpend: number;
    weeks: WeeklyMetric[];
  }
>;

export const getReschedulesByMentor: ApiDescription = {
  endpoint: '/admin/metrics/reschedules-by-mentor',
  method: RequestsMethod.Get,
};

export type GetReschedulesByMentor = ApiData<
  {
    month?: string;
  },
  {
    mentorId: string;
    mentorName: string;
    count: number;
    averageRescheduleLeadTimeMinutes: number;
    reasons: {
      reason: string;
      count: number;
    }[];
  }[]
>;

type DurationStat = {
  averageMinutes: number;
  sampleSize: number;
};

export const getDurationMetrics: ApiDescription = {
  endpoint: '/admin/metrics/durations',
  method: RequestsMethod.Get,
};

export type GetDurationMetrics = ApiData<
  {
    month?: string;
  },
  {
    paymentToAllocation: DurationStat;
    allocationToFirstSession: DurationStat;
  }
>;

export const getEducationStrategistTime: ApiDescription = {
  endpoint: '/admin/metrics/education-strategist-time',
  method: RequestsMethod.Get,
};

export type GetEducationStrategistTime = ApiData<
  {
    month?: string;
  },
  {
    strategistId: string;
    strategistName: string;
    sessions: number;
    overSeconds: number;
    underSeconds: number;
  }[]
>;

export const getEducationStrategistWorkload: ApiDescription = {
  endpoint: '/admin/metrics/education-strategist-workload',
  method: RequestsMethod.Get,
};

export type GetEducationStrategistWorkload = ApiData<
  {
    month?: string;
  },
  {
    strategistId: string;
    strategistName: string;
    isSalaried: boolean;
    isVatPayer: boolean;
    mentoringMeetings: number;
    mentoringHeldMeetings: number;
    mentoringScheduledMeetings: number;
    educationStrategyMeetings: number;
    onboardingMeetings: number;
    totalMeetings: number;
    salaryAmount: number;
    salaryCurrency: string;
    salaryInEur: number;
    salaryInEurWithVat: number;
  }[]
>;

type SystemMeetingCounts = {
  total: number;
  completed: number;
  scheduled: number;
  cancelled: number;
};

export const getSystemMeetingMetrics: ApiDescription = {
  endpoint: '/admin/metrics/system-meetings',
  method: RequestsMethod.Get,
};

export type GetSystemMeetingMetrics = ApiData<
  {
    month?: string;
  },
  {
    onboarding: SystemMeetingCounts;
    educationStrategy: SystemMeetingCounts & {
      byReason: (SystemMeetingCounts & { reason: EducationStrategyReason | 'unknown' })[];
    };
  }
>;

export const getUniversityResults: ApiDescription = {
  endpoint: '/admin/metrics/university-results',
  method: RequestsMethod.Get,
};

export type GetUniversityResults = ApiData<
  {
    applicationStages?: GoalStatusTypes[];
    majors?: string[];
    universities?: string[];
    graduationYears?: string[];
  },
  {
    universityId: string;
    universityName: string;
    count: number;
  }[]
>;

export const getSessionQualityScores: ApiDescription = {
  endpoint: '/admin/metrics/session-quality-scores',
  method: RequestsMethod.Get,
};

export type GetSessionQualityScores = ApiData<
  {
    month?: string;
  },
  SessionQualityScoreRowDTO[]
>;

export const getSessionQualityScoresExport: ApiDescription = {
  endpoint: '/admin/metrics/session-quality-scores/export',
  method: RequestsMethod.Get,
};

export type GetSessionQualityScoresExport = ApiData<
  {
    month?: string;
  },
  SessionQualityScoreExportRowDTO[]
>;

export const getMentorQualityScores: ApiDescription = {
  endpoint: '/admin/metrics/mentor-quality-scores',
  method: RequestsMethod.Get,
};

export type GetMentorQualityScores = ApiData<
  {
    mentorId?: string;
  },
  MentorQualityScoreDTO[]
>;

export const getQualityScoringRuns: ApiDescription = {
  endpoint: '/admin/metrics/quality-scoring-runs',
  method: RequestsMethod.Get,
};

export type GetQualityScoringRuns = ApiData<
  {
    month?: string;
  },
  QualityScoringRunDTO[]
>;

export const getAdmissionResults: ApiDescription = {
  endpoint: '/admin/metrics/admission-results',
  method: RequestsMethod.Get,
};

export type GetAdmissionResults = ApiData<unknown, { rows: AdmissionResultRowDTO[] }>;

export const getAdmissionTiers: ApiDescription = {
  endpoint: '/admin/metrics/admission-tiers',
  method: RequestsMethod.Get,
};

export type GetAdmissionTiers = ApiData<unknown, { universities: AdmissionTierUniversityDTO[] }>;

export const updateAdmissionTiers: ApiDescription = {
  endpoint: '/admin/metrics/admission-tiers/:id',
  method: RequestsMethod.Patch,
};

export type UpdateAdmissionTiers = ApiData<{ tiers: string[] }, AdmissionTierUniversityDTO>;

export const seedAdmissionTiers: ApiDescription = {
  endpoint: '/admin/metrics/admission-tiers/seed',
  method: RequestsMethod.Post,
};

export type SeedAdmissionTiers = ApiData<
  unknown,
  { updated: number; matched: string[]; unmatched: string[] }
>;
